import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Products } from '../../../core/services/products.service';
import { OrderService } from '../../../core/services/order.service';
import { AttributesService } from '../../../core/services/attributes.service';
import { ClientesService } from '../../../core/services/clientes.service';
import { TipoEnvioService } from '../../../core/services/tipo-envio.service';
import { TipoMonedaService } from '../../../core/services/tipo-moneda.service';
import { OrigenVentaService } from '../../../core/services/origen-venta.service';
import { UbigeoService, UbigeoItem } from '../../../core/services/ubigeo.service';
import { ManualOrderRequest, OrderItemDto } from '../../../shared/models/Order';
import { Product } from '../../../shared/models/Product';
import { Color } from '../../../shared/models/Attribute';
import { Cliente, OrigenVenta, TipoEnvio, TipoMoneda } from '../../../shared/models/system';

@Component({
  selector: 'app-new-order',
  standalone: true,
  imports: [
    CommonModule, FormsModule, AutoCompleteModule, InputTextModule,
    InputNumberModule, ButtonModule, TableModule, DatePickerModule,
    SelectModule, TextareaModule, ToastModule, DialogModule, RadioButtonModule, TooltipModule
  ],
  providers: [MessageService],
  templateUrl: './new-order.component.html',
  styleUrls: ['./new-order.component.css']
})
export class NewOrderComponent implements OnInit {

  clientName: string = '';
  clientPhone: string = '';
  city: string = 'Lima';
  deliveryPrice: number = 0;
  district: string = '';
  deliveryAddress: string = '';
  deliveryMethod: 'DELIVERY' | 'RECOJO_PUNTO' = 'DELIVERY';
  tipoEnvioId: number | null = null;
  scheduledDate: Date = new Date();
  scheduledTime: Date = new Date();
  notes: string = '';
  paymentMethod: string = 'YAPE';
  amountPaid: number = 0;
  cartItems: OrderItemDto[] = [];
  totalAmount: number = 0;
  filteredProducts: Product[] = [];
  selectedProduct: Product | null = null;
  showProductDialog: boolean = false;
  tempItem: Partial<OrderItemDto> = {};

  availableSizes: any[] = [];
  availableColors: Color[] = [];

  maxStock: number = 100;
  isEditMode: boolean = false;
  orderIdToEdit: number | null = null;

  origenesVenta: OrigenVenta[] = [];
  origenVentaId: number | null = null;
  origenVentaActual: OrigenVenta | null = null;
  saleOrigin: string = '';
  get origenVentaOptions(): OrigenVenta[] {
    const list = [...this.origenesVenta];
    if (this.origenVentaActual && !list.some(o => o.id === this.origenVentaActual!.id)) list.unshift(this.origenVentaActual);
    return list;
  }

  discountValue: number = 0;
  discountTypes = [
    { label: 'Soles (S/)', value: 'moneda' },
    { label: 'Porcentaje (%)', value: 'porcentaje' }
  ];
  discountType: string = 'moneda';
  totalItemsWithDiscount: number = 0;

  paymentMethods = [
    { label: 'Yape', value: 'YAPE' },
    { label: 'Plin', value: 'PLIN' },
    { label: 'Transferencia', value: 'TRANSFERENCIA' },
    { label: 'Efectivo', value: 'EFECTIVO' }
  ];

  filteredClientes: Cliente[] = [];
  selectedCliente: Cliente | null = null;
  tiposEnvio: TipoEnvio[] = [];
  tiposMoneda: TipoMoneda[] = [];
  tipoMonedaId: number | null = null;
  paises: UbigeoItem[] = [];
  departamentos: UbigeoItem[] = [];
  provincias: UbigeoItem[] = [];
  distritosUbigeo: UbigeoItem[] = [];
  paisId: number | null = null;
  departamentoId: number | null = null;
  provinciaId: number | null = null;
  distritoId: number | null = null;

  constructor(
    private productService: Products,
    private orderService: OrderService,
    private attrService: AttributesService,
    private clientesService: ClientesService,
    private tipoEnvioService: TipoEnvioService,
    private tipoMonedaService: TipoMonedaService,
    private origenVentaService: OrigenVentaService,
    private ubigeoService: UbigeoService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode = true;
      this.orderIdToEdit = Number(id);
      this.loadOrderData(this.orderIdToEdit);
    }
    this.loadColors();
    this.loadTiposEnvio();
    this.loadTiposMoneda();
    this.loadOrigenesVenta();
    this.loadPaises();
  }

  loadTiposMoneda(): void {
    this.tipoMonedaService.getActive().subscribe({
      next: (list) => { this.tiposMoneda = list; if (list?.length && !this.tipoMonedaId) this.tipoMonedaId = list[0].id; this.cd.detectChanges(); },
      error: () => {}
    });
  }

  loadOrigenesVenta(): void {
    this.origenVentaService.getActive().subscribe({
      next: (list) => { this.origenesVenta = list; this.cd.detectChanges(); },
      error: () => {}
    });
  }

  onOrigenVentaClear(): void {
    this.saleOrigin = '';
  }

  getMonedaSimbolo(): string {
    if (!this.tipoMonedaId || !this.tiposMoneda?.length) return 'S/';
    const t = this.tiposMoneda.find(x => x.id === this.tipoMonedaId);
    return t?.simbolo ?? 'S/';
  }

  get selectedTipoEnvio(): TipoEnvio | undefined {
    return this.tipoEnvioId != null ? this.tiposEnvio.find(t => t.id === this.tipoEnvioId) : undefined;
  }

  get showDateTimePicker(): boolean {
    const name = this.selectedTipoEnvio?.nombreTipoEnvio?.toLowerCase() ?? '';
    return name.includes('punto') || name.includes('shalom');
  }

  loadTiposEnvio(): void {
    this.tipoEnvioService.getActive().subscribe({
      next: (list) => { this.tiposEnvio = list; this.cd.detectChanges(); },
      error: () => {}
    });
  }

  loadPaises(): void {
    this.ubigeoService.getPaises().subscribe({
      next: (list) => {
        this.paises = list;
        if (!this.isEditMode && list?.length) {
          setTimeout(() => this.setDefaultUbigeoLima(), 0);
        }
      },
      error: () => {}
    });
  }

  private setDefaultUbigeoLima(): void {
    const peru = this.paises.find(p => (p.nombre || '').toLowerCase().includes('perú') || (p.nombre || '').toLowerCase() === 'peru');
    if (!peru) return;
    this.paisId = peru.id;
    this.ubigeoService.getDepartamentos(peru.id).subscribe({
      next: (depts) => {
        this.departamentos = depts;
        const lima = depts.find(d => (d.nombre || '').toLowerCase() === 'lima');
        if (lima) {
          this.departamentoId = lima.id;
          this.ubigeoService.getProvincias(lima.id).subscribe({
            next: (provs) => {
              this.provincias = provs;
              const limaProv = provs.find(p => (p.nombre || '').toLowerCase() === 'lima');
              if (limaProv) {
                this.provinciaId = limaProv.id;
                this.ubigeoService.getDistritos(limaProv.id).subscribe({
                  next: (distritos) => {
                    this.distritosUbigeo = distritos;
                    this.cd.detectChanges();
                  }
                });
              } else this.cd.detectChanges();
            }
          });
        } else this.cd.detectChanges();
      }
    });
  }

  onTipoEnvioChange(): void {
    const t = this.selectedTipoEnvio;
    if (t) {
      const n = (t.nombreTipoEnvio || '').toLowerCase();
      if (n.includes('delivery') || n.includes('domicilio')) this.deliveryMethod = 'DELIVERY';
      else if (n.includes('punto') || n.includes('recojo')) this.deliveryMethod = 'RECOJO_PUNTO';
    }
  }

  onPaisChange(): void {
    this.departamentoId = null; this.provinciaId = null; this.distritoId = null;
    this.provincias = []; this.distritosUbigeo = [];
    if (this.paisId) {
      this.ubigeoService.getDepartamentos(this.paisId).subscribe({ next: (d) => { this.departamentos = d; this.cd.detectChanges(); } });
    } else this.departamentos = [];
  }

  onDepartamentoChange(): void {
    this.provinciaId = null; this.distritoId = null;
    this.distritosUbigeo = [];
    if (this.departamentoId) {
      this.ubigeoService.getProvincias(this.departamentoId).subscribe({ next: (p) => { this.provincias = p; this.cd.detectChanges(); } });
    } else this.provincias = [];
  }

  onProvinciaChange(): void {
    this.distritoId = null;
    if (this.provinciaId) {
      this.ubigeoService.getDistritos(this.provinciaId).subscribe({ next: (d) => { this.distritosUbigeo = d; this.district = ''; this.cd.detectChanges(); } });
    } else this.distritosUbigeo = [];
  }

  onDistritoUbigeoChange(): void {
    const d = this.distritosUbigeo.find(x => x.id === this.distritoId);
    if (d) this.district = d.nombre;
    this.cd.detectChanges();
  }

  filterClientes(event: { query: string }): void {
    const q = (event.query || '').toLowerCase();
    this.clientesService.getActive().subscribe({
      next: (list) => {
        this.filteredClientes = list.filter(c =>
          (c.nombreCliente || '').toLowerCase().includes(q) || (c.celular || '').includes(q)
        );
        this.cd.detectChanges();
      },
      error: () => { this.filteredClientes = []; }
    });
  }

  onClienteSelect(cliente: Cliente): void {
    this.clientName = cliente.nombreCliente || '';
    this.clientPhone = cliente.celular || '';
    this.selectedCliente = cliente;
    this.applyClienteDireccion(cliente);
    this.cd.detectChanges();
  }

  private applyClienteDireccion(cliente: Cliente): void {
    if (cliente.direccion) {
      this.deliveryAddress = cliente.direccion;
    }
    if (!cliente.distritoId) return;

    this.ubigeoService.getDistritoConPadres(cliente.distritoId).subscribe({
      next: (padres) => {
        this.paisId = padres.paisId ?? null;
        this.departamentoId = padres.departamentoId ?? null;
        this.provinciaId = padres.provinciaId ?? null;
        this.distritoId = cliente.distritoId ?? null;
        this.district = cliente.distritoNombre || this.district;

        const loadChain = () => {
          if (this.paisId) {
            this.ubigeoService.getDepartamentos(this.paisId).subscribe({
              next: (d) => {
                this.departamentos = d;
                if (this.departamentoId) {
                  this.ubigeoService.getProvincias(this.departamentoId).subscribe({
                    next: (p) => {
                      this.provincias = p;
                      if (this.provinciaId) {
                        this.ubigeoService.getDistritos(this.provinciaId).subscribe({
                          next: (dis) => {
                            this.distritosUbigeo = dis;
                            const d = dis.find(x => x.id === this.distritoId);
                            if (d) this.district = d.nombre;
                            this.cd.detectChanges();
                          }
                        });
                      } else this.cd.detectChanges();
                    }
                  });
                } else this.cd.detectChanges();
              }
            });
          }
        };
        loadChain();
      },
      error: () => {
        // Fallback: solo texto de dirección
        if (cliente.distritoNombre) this.district = cliente.distritoNombre;
        this.cd.detectChanges();
      }
    });
  }

  get selectedClienteEmail(): string {
    return this.selectedCliente?.email?.trim() || '';
  }

  loadColors() {
    this.attrService.getActiveColors().subscribe({
      next: (colors) => {
        this.availableColors = colors;
      },
      error: (e) => console.error('Error cargando colores', e)
    });
  }

  filterProducts(event: any) {
    const query = (event?.query || '').trim();
    if (query.length < 2) {
      this.filteredProducts = [];
      this.cd.detectChanges();
      return;
    }
    this.productService.searchProducts(query).subscribe({
      next: (products) => {
        this.filteredProducts = products;
        this.cd.detectChanges();
      },
      error: () => { this.filteredProducts = []; this.cd.detectChanges(); }
    });
  }

  onProductSelect(event: any) {
    const product = event.value as Product;
    this.tempItem = {
      productId: product.id,
      productName: product.name,
      image: product.image,
      quantity: 1,
      unitPrice: (product.isOffer && product.priceOffer) ? product.priceOffer : product.price
    };


    this.tempItem.color = '';

    if (product.color) {
        const match = this.availableColors.find(c => c.hexCode === product.color);
        if (match) {
            this.tempItem.color = match.name;
        } else if (product.colorName) {
            this.tempItem.color = product.colorName;
        }
    }

    if (product.stockDetails && product.stockDetails.length > 0) {
        this.availableSizes = product.stockDetails.map(sd => ({
            label: `${sd.sizeName} (${sd.quantity})`,
            value: sd.sizeName,
            disabled: sd.quantity <= 0,
            stock: sd.quantity,
            price: sd.price != null ? Number(sd.price) : (product.price ?? 0)
        }));
    } else {
        this.availableSizes = product.sizeNames ? product.sizeNames.map(s => ({
            label: s, value: s, disabled: false, stock: 100, price: product.price ?? 0
        })) : [];
    }

    this.maxStock = 100;
    this.showProductDialog = true;
    this.selectedProduct = null;
  }

  updateMaxStock() {
      const selectedSizeObj = this.availableSizes.find(s => s.value === this.tempItem.size);
      if (selectedSizeObj) {
          this.maxStock = selectedSizeObj.stock;
          if (selectedSizeObj.price != null) {
              this.tempItem.unitPrice = selectedSizeObj.price;
          }
          if (this.tempItem.quantity && this.tempItem.quantity > this.maxStock) {
              this.tempItem.quantity = this.maxStock;
          }
      }
  }

  addToCart() {
    if (!this.tempItem.size) {
      this.messageService.add({severity:'warn', summary:'Falta Talla', detail:'Selecciona una talla'});
      return;
    }
    if (this.maxStock === 0) {
        this.messageService.add({severity:'error', summary:'Sin Stock', detail:'Esta talla no tiene stock disponible'});
        return;
    }

    const qty = this.tempItem.quantity || 1;
    const price = this.tempItem.unitPrice || 0;
    const subTotal = qty * price;

    this.cartItems.push({
      ...this.tempItem as OrderItemDto,
      quantity: qty,
      subTotal: subTotal
    });

    this.calculateTotal();
    this.showProductDialog = false;
    this.messageService.add({severity:'success', summary:'Agregado', detail:'Producto añadido'});
  }

  removeItem(index: number) {
    this.cartItems.splice(index, 1);
    this.calculateTotal();
  }

  calculateTotal() {
    let subtotalPrendas = this.cartItems.reduce((acc, item) => acc + (item.subTotal || 0), 0);
    if (this.discountValue && this.discountValue > 0) {
      if (this.discountType === 'porcentaje') {
        let descuentoCalculado = subtotalPrendas * (this.discountValue / 100);
        this.totalItemsWithDiscount = subtotalPrendas - descuentoCalculado;
      } else {
        this.totalItemsWithDiscount = subtotalPrendas - this.discountValue;
      }
    } else {
      this.totalItemsWithDiscount = subtotalPrendas;
    }

    if (this.totalItemsWithDiscount < 0) {
      this.totalItemsWithDiscount = 0;
    }

    this.totalAmount = this.totalItemsWithDiscount;
  }

  getSubtotalProductos(): number {
    return this.cartItems.reduce((acc, item) => acc + (item.subTotal || 0), 0);
  }

  getDescuentoCalculado(): number {
    const sub = this.getSubtotalProductos();
    if (this.discountType === 'porcentaje' && this.discountValue > 0) return sub * (this.discountValue / 100);
    return this.discountValue > 0 ? this.discountValue : 0;
  }

  getPendientePagar(): number {
    const total = this.totalAmount || 0;
    const pagado = this.amountPaid || 0;
    const pendiente = total - pagado;
    return pendiente > 0 ? pendiente : 0;
  }

  loadOrderData(id: number) {
    this.orderService.getOrderById(id).subscribe({
      next: (order) => {
        this.clientName = order.clientName;
        this.clientPhone = order.clientPhone;
        this.city = order.city;
        this.district = order.district ?? order.distrito?.nombre ?? '';
        this.deliveryPrice = order.deliveryPrice ?? 0;
        this.deliveryAddress = order.deliveryAddress;
        this.deliveryMethod = order.deliveryMethod as any;
        this.notes = order.notes ?? '';

        this.saleOrigin = order.saleOrigin ?? '';
        this.origenVentaActual = order.origenVenta ? { id: order.origenVenta.id, nombreOrigenVenta: order.origenVenta.nombreOrigenVenta } : null;
        this.origenVentaId = order.origenVenta?.id ?? null;
        this.discountValue = Number(order.discountAmount) ?? 0;
        this.discountType = 'moneda';
        this.amountPaid = order.amountPaid ?? 0;

        if (order.tipoEnvio?.id != null) this.tipoEnvioId = order.tipoEnvio.id;
        if ((order as any).tipoMonedaId != null) this.tipoMonedaId = (order as any).tipoMonedaId;
        else if (order.tipoMoneda?.id != null) this.tipoMonedaId = order.tipoMoneda.id;
        if ((order as any).distritoId != null) this.distritoId = (order as any).distritoId;
        else if (order.distrito?.id != null) this.distritoId = order.distrito.id;

        if (order.distrito?.id != null) {
          this.ubigeoService.getDistritoConPadres(order.distrito.id).subscribe({
            next: (dp) => {
              this.paisId = dp.paisId;
              this.departamentoId = dp.departamentoId;
              this.provinciaId = dp.provinciaId;
              this.ubigeoService.getDepartamentos(dp.paisId).subscribe({ next: (d) => { this.departamentos = d; this.cd.detectChanges(); } });
              this.ubigeoService.getProvincias(dp.departamentoId).subscribe({ next: (p) => { this.provincias = p; this.cd.detectChanges(); } });
              this.ubigeoService.getDistritos(dp.provinciaId).subscribe({ next: (list) => { this.distritosUbigeo = list; this.cd.detectChanges(); } });
            }
          });
        }

        if (order.scheduledDate) {
          this.scheduledDate = new Date(order.scheduledDate + 'T00:00:00');
        }
        if (order.scheduledTime) {
          const today = new Date();
          const [hours, minutes] = order.scheduledTime.split(':');
          today.setHours(Number(hours), Number(minutes), 0);
          this.scheduledTime = today;
        }

        this.paymentMethod = order.paymentMethod;

        this.cartItems = order.items.map((item: any) => ({
          productId: item.product?.id || item.productId || 0,
          productName: item.product?.name || item.product?.productName || item.productName || 'Producto',
          image: item.product?.image || item.product?.productImage || item.image,
          quantity: item.quantity,
          size: item.size,
          color: item.color,
          unitPrice: item.unitPrice != null ? Number(item.unitPrice) : undefined,
          subTotal: item.subTotal != null ? Number(item.subTotal) : (item.quantity * (item.unitPrice || 0))
        }));

        this.calculateTotal();
        this.cd.detectChanges();
      },
      error: (err) => {
        this.messageService.add({severity:'error', summary:'Error', detail:'No se pudo cargar el pedido'});
        this.router.navigate(['/admin/orders/active']);
      }
    });
  }

  saveOrder() {
    if (this.cartItems.length === 0) {
      this.messageService.add({severity:'error', summary:'Carrito Vacío', detail:'Agrega al menos un producto'});
      return;
    }
    if (!this.clientName || !this.clientPhone) {
      this.messageService.add({severity:'error', summary:'Datos Faltantes', detail:'Nombre y Celular son obligatorios'});
      return;
    }

    const dateStr = this.scheduledDate.toISOString().split('T')[0];
    const timeStr = this.showDateTimePicker
      ? this.scheduledTime.toTimeString().split(' ')[0].substring(0, 5)
      : '00:00';

    const payload: ManualOrderRequest = {
      clientName: this.clientName,
      clientPhone: this.clientPhone,
      clienteId: this.selectedCliente?.id ?? undefined,
      city: this.city,
      district: this.district,
      deliveryAddress: this.deliveryAddress,
      deliveryMethod: this.deliveryMethod,
      tipoEnvioId: this.tipoEnvioId ?? undefined,
      distritoId: this.distritoId ?? undefined,
      date: dateStr,
      time: timeStr,
      notes: this.notes,

      saleOrigin: this.origenVentaId != null ? undefined : this.saleOrigin,
      origenVentaId: this.origenVentaId ?? undefined,

      discountAmount: this.discountType === 'porcentaje'
        ? (this.cartItems.reduce((acc, item) => acc + (item.subTotal || 0), 0) * (this.discountValue / 100))
        : this.discountValue,

      paymentMethod: this.paymentMethod,
      deliveryPrice: this.deliveryPrice,
      amountPaid: this.amountPaid,
      tipoMonedaId: this.tipoMonedaId ?? undefined,

      customTotalAmount: this.totalAmount,

      items: this.cartItems
    };

    if (this.isEditMode && this.orderIdToEdit) {
      this.orderService.updateOrder(this.orderIdToEdit, payload).subscribe({
        next: () => {
          this.messageService.add({severity:'success', summary:'Actualizado', detail:'Pedido corregido correctamente'});
          setTimeout(() => this.router.navigate(['/admin/orders/active']), 1000);
        },
        error: () => this.messageService.add({severity:'error', summary:'Error', detail:'No se pudo actualizar'})
      });
    } else {
      this.orderService.createManualOrder(payload).subscribe({
        next: () => {
          this.messageService.add({severity:'success', summary:'Creado', detail:'Venta registrada'});
          setTimeout(() => this.router.navigate(['/admin/orders/active']), 1000);
        },
        error: (err) => {
          const msg = err?.error?.message || 'No se pudo crear';
          this.messageService.add({severity:'error', summary:'Error', detail: msg});
        }
      });
    }
  }
}